'use client';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { forwardRef } from 'react';
import { UnstyledButton, createScopedKeydownHandler } from '@s7/base';
import { useComponentDefaultProps } from '@s7/styles';
import { COMPONENT_ID } from '../../Accordion.contract.js';
import { useAccordionContext } from '../../context/Accordion.context.js';
import { useAccordionItemContext } from '../AccordionItem.context.js';
import { controlDefaultProps } from './AccordionControl.contract.js';
import useStyles from './AccordionControl.styles.js';
import { Typography } from '../../../Typography/Typography.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var AccordionControl = /*#__PURE__*/ forwardRef(function(props, ref) {
    var _useComponentDefaultProps = useComponentDefaultProps(COMPONENT_ID.ACCORDION_CONTROL, controlDefaultProps, props), disabled = _useComponentDefaultProps.disabled, onKeyDown = _useComponentDefaultProps.onKeyDown, onClick = _useComponentDefaultProps.onClick, chevron = _useComponentDefaultProps.chevron, children = _useComponentDefaultProps.children, className = _useComponentDefaultProps.className, others = _object_without_properties_loose(_useComponentDefaultProps, [
        "disabled",
        "onKeyDown",
        "onClick",
        "chevron",
        "children",
        "className"
    ]);
    var context = useAccordionContext();
    var value = useAccordionItemContext().value;
    var _useStyles = useStyles({
        params: {
            transitionDuration: context.transitionDuration,
            chevronPosition: context.chevronPosition,
            chevronSize: context.chevronSize,
            radius: context.radius
        },
        options: {
            name: COMPONENT_ID.ACCORDION,
            classNames: context.classNames,
            styles: context.styles,
            variations: {
                variant: context.variant
            }
        },
        theme: context.theme
    }), classes = _useStyles.classes, cx = _useStyles.cx;
    var isActive = context.isItemActive(value);
    return /*#__PURE__*/ jsxs(UnstyledButton, _extends({}, others, {
        ref: ref,
        "aria-controls": context.getRegionId(value),
        "aria-expanded": isActive,
        className: cx(classes.control, className),
        "data-active": isActive || undefined,
        disabled: disabled,
        id: context.getControlId(value),
        type: "button",
        "data-accordion-control": true,
        onClick: function(event) {
            onClick == null ? void 0 : onClick(event);
            context.onChange(value);
        },
        onKeyDown: createScopedKeydownHandler({
            siblingSelector: "[data-accordion-control]",
            parentSelector: "[data-accordion]",
            activateOnFocus: false,
            loop: context.loop,
            orientation: "vertical",
            onKeyDown: onKeyDown
        }),
        children: [
            /*#__PURE__*/ jsx("span", {
                "data-rotate": !context.disableChevronRotation && isActive || undefined,
                "aria-label": isActive ? "свернуть" : "развернуть",
                className: classes.chevron,
                role: "figure",
                children: chevron || context.chevron
            }),
            /*#__PURE__*/ jsx(Typography, _extends({
                className: classes.label,
                size: "sm1",
                variant: "heading",
                weight: "semibold",
                truncate: true
            }, context.labelProps, {
                children: children
            }))
        ]
    }));
});
AccordionControl.displayName = "Accordion.Item.Control";

export { AccordionControl };
